import React, { useMemo } from 'react';
import cx from 'classnames';
import { scaleLinear } from '@visx/scale';
import { useParentSize } from '@visx/responsive';
import RangeSpan from 'js/engines/CapabilitiesAssessment/components/Review/Charts/CapabilityResultsChart/RangeSpan';
import styles from './CapabilityOverviewCharts.module.scss';

const margin = {
  top: 8,
  right: 0,
  bottom: 8,
  left: 0,
};

const DEFAULT_HEIGHT = 160;

export default function CapabilityOverviewBarSvg({
  metrics,
  element,
  threshold,
  width: widthOverride,
  height = DEFAULT_HEIGHT,
  children,
  itemIndex = 0,
}) {
  const { parentRef, width: parentWidth } = useParentSize({ debounceTime: 0 });
  const width =
    typeof widthOverride === 'number' && Number.isFinite(widthOverride)
      ? widthOverride
      : parentWidth || 0;
  const chartWidth = Math.max(0, width - margin.left - margin.right);
  const chartHeight = height - margin.top - margin.bottom;

  const scaleY = useMemo(
    () =>
      scaleLinear({
        domain: [0, 1],
        range: [chartHeight, 0],
      }),
    [chartHeight]
  );

  const hasScore =
    metrics &&
    typeof metrics.score === 'number' &&
    Number.isFinite(metrics.score);
  const hasRange =
    hasScore &&
    typeof metrics.minScore === 'number' &&
    Number.isFinite(metrics.minScore) &&
    typeof metrics.maxScore === 'number' &&
    Number.isFinite(metrics.maxScore);

  const paletteIndex = element?.paletteIndex ?? 2;
  const hasThreshold =
    typeof threshold === 'number' &&
    Number.isFinite(threshold) &&
    threshold >= 0 &&
    threshold <= 1;

  return (
    <div ref={parentRef} className={styles['svg-wrap']}>
      {width > 0 && (
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width={widthOverride ?? '100%'}
          height={height}
          className={`palette-chart-${paletteIndex} chart-light`}
        >
          <g transform={`translate(${margin.left}, ${margin.top})`}>
            {hasThreshold && (
              <line
                x1={0}
                y1={scaleY(threshold)}
                x2={chartWidth}
                y2={scaleY(threshold)}
                className={cx(
                  'chart-median chart-light palette-stroke',
                  styles['threshold-line']
                )}
                style={{ strokeDashoffset: (itemIndex * chartWidth) % 8 }}
              />
            )}

            <line
              x1={0}
              y1={0}
              x2={chartWidth}
              y2={0}
              className="chart-axis axis-solid"
            />
            <line
              x1={0}
              y1={chartHeight}
              x2={chartWidth}
              y2={chartHeight}
              className="chart-axis axis-solid"
            />
            {hasRange && (
              <RangeSpan
                x={chartWidth / 2 - 4}
                width={8}
                min={scaleY(metrics.maxScore)}
                max={scaleY(metrics.minScore)}
                minimumDifference={12}
                absoluteMin={0}
                absoluteMax={chartHeight}
              />
            )}
            {hasScore && (
              <circle
                cx={chartWidth / 2}
                cy={scaleY(metrics.score)}
                r={7}
                className="palette-fill"
              />
            )}
            {!hasScore && (
              <circle
                cx={chartWidth / 2}
                cy={scaleY(0.5)}
                r={5}
                fill="none"
                stroke="currentColor"
                strokeWidth={1}
                opacity={0.35}
              />
            )}
          </g>
        </svg>
      )}
      {children}
    </div>
  );
}
